import {
  isServiceRequestStatusEditable,
  SERVICE_REQUEST_ATTENDANCE_COLORS,
  SERVICE_REQUEST_ATTENDANCE_ICON_COLORS,
} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import PERMISSIONS from '@/constants/permissions';
import { editServiceRequestRequest } from '@/services/api/serviceRequest';
import {
  ServiceRequestAttendanceStatus,
  ServiceRequestModel,
} from '@/typings/models/ServiceRequest';
import { useHasAccess } from '@/utils/hooks';
import { F } from '@/utils/intl';
import { isCallbackFromLocation } from '@/utils/serviceRequest';
import { processAPIResponseNow } from '@/utils/request';
import {
  DownOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Dropdown, message, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useState } from 'react';
import { useDispatch, useIntl } from 'umi';
import styles from './index.less';

/** Customer attendance of a service request; null = not set. */
export type AttendanceStatus = ServiceRequestModel['attendance_status'];

// Reuses the existing labels ("Erschienen" / "Nicht erschienen"), which the
// lead activity log already shows for attendance changes.
const LABELS = {
  [ServiceRequestAttendanceStatus.show]: {
    id: 'pages.serviceRequest.attended',
    defaultMessage: 'Attended',
  },
  [ServiceRequestAttendanceStatus.no_show]: {
    id: 'pages.serviceRequest.noShow',
    defaultMessage: 'No Show',
  },
};
const NOT_SET_LABEL = {
  id: 'pages.serviceRequest.attendanceNotSetLabel',
  defaultMessage: 'Not set',
};
const RESET_KEY = 'reset';

/** Attendance can only be known once the meeting has begun. */
export const hasMeetingStarted = (
  appointment?: Pick<ServiceRequestModel, 'utc_start_time' | 'real_start_time'> & Partial<Pick<ServiceRequestModel, 'service'>>,
  now: Dayjs = dayjs(),
): boolean => {
  if (isCallbackFromLocation(appointment)) return false;
  const start = appointment?.real_start_time || appointment?.utc_start_time;
  return Boolean(start) && !dayjs(start).isAfter(now);
};

/** Same pill as ServiceRequestStatusTag, so both statuses read alike. */
export const AttendanceTag: React.FC<{ status?: AttendanceStatus }> = ({
  status,
}) => (
  <Tag
    color={status ? SERVICE_REQUEST_ATTENDANCE_COLORS[status] : undefined}
    style={{ borderRadius: '100px', border: 'none' }}
  >
    <F {...(status ? LABELS[status] : NOT_SET_LABEL)} />
  </Tag>
);

/**
 * Compact read-only marker for the calendar card; nothing when not set. A
 * person icon (not ✓ / ✕), so "no show" can't be read as "cancelled".
 */
export const AttendanceIcon: React.FC<{ status?: AttendanceStatus }> = ({
  status,
}) => {
  const intl = useIntl();
  if (!status) return null;
  const label = intl.formatMessage(LABELS[status]);
  const Icon =
    status === ServiceRequestAttendanceStatus.show
      ? UserOutlined
      : UserDeleteOutlined;
  return (
    <Tooltip title={label}>
      <Icon
        role="img"
        aria-label={label}
        style={{ color: SERVICE_REQUEST_ATTENDANCE_ICON_COLORS[status] }}
      />
    </Tooltip>
  );
};

/** Whether the current user may change this appointment's attendance. */
export const useCanEditAttendance = (appointment?: ServiceRequestModel) => {
  const hasPermission = useHasAccess([
    PERMISSIONS.ServiceRequests.MANAGE_ATTENDANCE,
    PERMISSIONS.BUSINESS_PARTNER,
  ]);
  return hasPermission && isServiceRequestStatusEditable(appointment);
};

/** Whether the control renders anything (so a host can skip its own row). */
export const hasAttendanceToShow = (
  appointment: ServiceRequestModel | undefined,
  editable: boolean,
) =>
  Boolean(appointment) &&
  !appointment!.blocker &&
  hasMeetingStarted(appointment) &&
  (editable || Boolean(appointment!.attendance_status));

/**
 * The attendance of a started service request as one small pill. Permitted
 * users click it to set, switch or clear the status in a menu; everyone else
 * sees the status as a plain tag (nothing while it is not set). Renders nothing
 * for calendar events and meetings that have not started yet.
 */
const AttendanceControl: React.FC<{ appointment: ServiceRequestModel }> = ({
  appointment,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const editable = useCanEditAttendance(appointment);
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (status: AttendanceStatus) => {
      setSaving(true);
      const defaultErrorMessage = intl.formatMessage({
        id: 'pages.serviceRequest.attendanceSaveFailed',
        defaultMessage: 'Could not save attendance',
      });
      try {
        // Resolves with {response, data} also on 4xx/5xx; processAPIResponseNow
        // shows the server's message for those and only calls onSuccess on 2xx.
        const result = await editServiceRequestRequest({
          params: { serviceRequestId: appointment.id },
          data: { attendance_status: status },
        });
        processAPIResponseNow(result, {
          defaultErrorMessage,
          onSuccess() {
            // Upserts the event into the shared calendarEvents store, so the
            // card, overlay, chip and drawer all re-render with the new status.
            dispatch({
              type: 'serviceRequest/fetchSingleAppointment',
              payload: { appointmentId: appointment.id },
            });
          },
        });
      } catch (e) {
        message.error(defaultErrorMessage);
      } finally {
        setSaving(false);
      }
    },
    [appointment?.id, dispatch, intl],
  );

  if (!hasAttendanceToShow(appointment, editable)) return null;

  const status: AttendanceStatus = appointment.attendance_status ?? null;
  if (!editable) return <AttendanceTag status={status} />;

  const { show, no_show: noShow } = ServiceRequestAttendanceStatus;
  const items = [
    { key: show, label: <F {...LABELS[show]} /> },
    { key: noShow, label: <F {...LABELS[noShow]} /> },
    ...(status
      ? [
          { type: 'divider' as const },
          {
            key: RESET_KEY,
            label: <F id="common.actions.reset" defaultMessage="Reset" />,
          },
        ]
      : []),
  ];

  return (
    <Dropdown
      trigger={['click']}
      disabled={saving}
      // Inside the hover overlay and the chip panel the menu must stay in
      // their DOM, or their outside-click handling closes them on a selection.
      getPopupContainer={(node) => node.parentNode as HTMLElement}
      menu={{
        items,
        selectable: true,
        selectedKeys: status ? [status] : [],
        onClick: ({ key }) =>
          save(key === RESET_KEY ? null : (key as AttendanceStatus)),
      }}
    >
      <button
        type="button"
        disabled={saving}
        className={classNames(styles.pill, {
          [styles.show]: status === show,
          [styles.noShow]: status === noShow,
        })}
      >
        {status ? (
          <F {...LABELS[status]} />
        ) : (
          <F
            id="pages.lead.fields.appointment:attendanceStatus"
            defaultMessage="Attendance"
          />
        )}
        <DownOutlined className={styles.caret} />
      </button>
    </Dropdown>
  );
};

export default AttendanceControl;
